import { SvgIcon, Typography } from '@mui/material'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import tw from 'twin.macro'
import { colors } from 'src/theme'
import { createAsyncAction } from 'src/utils/reduxUtils'
import {
  CustomerStitchStartFlow,
  CustomerStitchCompletePayment,
  FetchCustomerProfile,
} from 'src/constants/actionTypes'
import { showToast } from 'src/utils/toast'
import { CancelMajor } from '@shopify/polaris-icons'
import qs from 'query-string'
import _ from 'lodash'
import { StitchIframeMessage } from 'src/constants/stitch'
import { getUser } from 'src/selectors/user'
import { getIsCustomerOnboardingPaymentInformationCompleted } from 'src/selectors/customer'
import { Button } from 'src/components/shared/Button'
import { customerComplianceStepTitles } from 'src/constants/customer/constants'
import { CustomerResubmitInfoType } from 'src/constants/customer/enums'
import { customerComplianceStepIcons } from 'src/constants/customer/icons'
import { CustomerFormHeader } from './shared/CustomerFormHeader'
import { usePolling } from 'src/utils/hooks/usePolling'
import { logger } from 'src/utils/logger'

interface Props {
  stepNumber?: number
  stepCount?: number
  onSubmit?: () => void
}

const CustomerStitchPaymentComponent = ({ stepNumber, stepCount, onSubmit }: Props) => {
  const dispatch = useDispatch()

  const user = useSelector(getUser)
  const isPaymentCompleted = useSelector(getIsCustomerOnboardingPaymentInformationCompleted)

  const [stitchToken, setStitchToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isProcessing, setIsProcessing] = useState<boolean>(false)

  const handleStitchCancel = useCallback(() => {
    setStitchToken(null)
  }, [])

  const handleStitchComplete = useCallback(async () => {
    try {
      await createAsyncAction(dispatch, CustomerStitchCompletePayment.request())
      setIsProcessing(true)
    } catch (err) {
      logger.track('onboarding_stitch_complete_failed')
      showToast('Failed to complete payment step, please try again later', {
        variant: 'error',
      })
    }

    setStitchToken(null)
  }, [])

  const handleStitchStart = useCallback(async () => {
    setIsLoading(true)

    try {
      const { token }: { token: string } = await createAsyncAction(
        dispatch,
        CustomerStitchStartFlow.request(),
      )

      if (token) {
        setStitchToken(token)
      } else {
        throw new Error('no token')
      }
    } catch (err) {
      logger.track('onboarding_stitch_load_failed')
      showToast('Failed to load payment page, please try again later', {
        variant: 'error',
      })
    }

    setIsLoading(false)
  }, [handleStitchComplete])

  useEffect(() => {
    const listener = (e: MessageEvent) => {
      // TODO: Once webhooks are ready, we should send a request to the server to make stitch payment step as in_progress
      if (
        e.data === StitchIframeMessage.AccountAdded ||
        e.data === StitchIframeMessage.ManualReview
      ) {
        handleStitchComplete()
      }
    }

    window.addEventListener('message', listener)

    return () => {
      window.removeEventListener('message', listener)
    }
  }, [handleStitchComplete])

  const pollForCompletion = useCallback(async () => {
    await createAsyncAction(dispatch, FetchCustomerProfile.request())
  }, [dispatch])

  usePolling({
    interval: 5000,
    shouldStart: () => isProcessing && !isPaymentCompleted,
    shouldStop: () => isPaymentCompleted,
    callback: pollForCompletion,
    stopOnSuccess: true,
  })

  useEffect(() => {
    if (isProcessing && isPaymentCompleted) {
      setIsProcessing(false)

      if (onSubmit) {
        onSubmit()
      }
    }
  }, [isProcessing, isPaymentCompleted, onSubmit])

  const iframeSource = useMemo(() => {
    if (!stitchToken) {
      return null
    }

    const query = {
      auth: stitchToken,
      email: user?.email,
      first_name: user?.firstName,
      last_name: user?.lastName,
    }

    const queryString = qs.stringify(_.omitBy(query, _.isNil))

    return `${process.env.REACT_APP_STITCH_URL}/?${queryString}`
  }, [stitchToken, user])

  return (
    <div css={[tw`w-[318px] bg-white rounded-lg flex-1 max-h-[75%] flex flex-col justify-between`]}>
      <CustomerFormHeader
        title={stepCount ? undefined : 'Resubmit info'}
        stepCount={stepCount}
        stepNumber={stepNumber}
      />
      <div>
        <div css={tw`px-4 pt-6 py-2`}>
          {customerComplianceStepIcons[CustomerResubmitInfoType.PaymentMethod]}
          <Typography variant="h4" css={tw`mt-2 mb-2`}>
            {customerComplianceStepTitles[CustomerResubmitInfoType.PaymentMethod]}
          </Typography>
          <div css={tw`w-full h-[1px] bg-[#EDEDED]`} />
          <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`mb-4 mt-2`}>
            To set up your payments we'll use our partner, Stitch. Tap the button below to begin.
          </Typography>
          <Typography variant="body2" color={colors.SHADES_GREY_400}>
            Once you've completed your payment setup, we'll automatically advance you to the next
            step.
          </Typography>
        </div>
      </div>
      <div css={tw`px-4 py-3 mt-auto`}>
        <Button
          className={tw`w-full`}
          size="small"
          loading={isLoading || isProcessing}
          onClick={handleStitchStart}
        >
          {isProcessing ? 'Processing...' : 'Get started'}
        </Button>
      </div>
      {iframeSource && (
        <div css={tw`fixed w-full h-full top-0 left-0 z-20`}>
          <iframe css={tw`w-full h-full border-0`} src={iframeSource} />
          <SvgIcon
            css={tw`absolute right-5 top-5 w-5 h-5 z-20 cursor-pointer`}
            onClick={handleStitchCancel}
          >
            <CancelMajor color={colors.GRAY_DARK_COOL} />
          </SvgIcon>
        </div>
      )}
    </div>
  )
}

export const CustomerStitchPayment = memo(CustomerStitchPaymentComponent)
